import type { FC, ReactNode } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { MobileNavItem } from './mobile-nav-item'
import type { Item } from '../config'

const renderItems = ({
  depth = 0,
  items,
  pathname,
}: {
  depth?: number
  items: Item[]
  pathname?: string | null
}): JSX.Element[] =>
  items.reduce(
    (acc: JSX.Element[], item) =>
      reduceChildRoutes({
        acc,
        depth,
        item,
        pathname,
      }),
    []
  )

const reduceChildRoutes = ({
  acc,
  depth,
  item,
  pathname,
}: {
  acc: JSX.Element[]
  depth: number
  item: Item
  pathname?: string | null
}): Array<JSX.Element> => {
  const checkPath = !!(item.path && pathname)
  const partialMatch = checkPath ? pathname.includes(item.path!) : false
  const leafActive =
    checkPath &&
    (item.matchPathPrefix
      ? pathname === item.path || pathname.startsWith(`${item.path!}/`)
      : pathname === item.path)

  if (item.items) {
    const visibleChildItems = item.items.filter(
      (child) => child.path && !child.disabled
    )

    acc.push(
      <MobileNavItem
        active={partialMatch}
        depth={depth}
        disabled={item.disabled}
        icon={item.icon}
        key={item.title}
        label={item.label}
        open={partialMatch}
        title={item.title}
      >
        <Stack
          component="ul"
          spacing={0.5}
          sx={{
            listStyle: 'none',
            m: 0,
            p: 0,
          }}
        >
          {renderItems({
            depth: depth + 1,
            items: visibleChildItems,
            pathname,
          })}
        </Stack>
      </MobileNavItem>
    )
  } else {
    acc.push(
      <MobileNavItem
        active={leafActive}
        depth={depth}
        disabled={item.disabled}
        external={item.external}
        icon={item.icon}
        key={item.title}
        label={item.label}
        path={item.path}
        title={item.title}
      />
    )
  }

  return acc
}

interface MobileNavSectionProps {
  items?: Item[]
  pathname?: string | null
  subheader?: string
}

export const MobileNavSection: FC<MobileNavSectionProps> = (props) => {
  const { items = [], pathname, subheader = '', ...other } = props

  return (
    <Stack
      component="ul"
      spacing={0.5}
      sx={{
        listStyle: 'none',
        m: 0,
        p: 0,
      }}
      {...other}
    >
      {subheader && (
        <Box
          component="li"
          sx={{
            color: 'var(--nav-section-title-color)',
            fontSize: 14,
            fontWeight: 700,
            lineHeight: 1.66,
            mb: 1,
            ml: 1,
            textTransform: 'uppercase',
          }}
        >
          {subheader}
        </Box>
      )}
      {renderItems({ items, pathname })}
    </Stack>
  )
}
